import { useEffect, forwardRef, useCallback, useMemo, MouseEvent, ForwardedRef, useState, KeyboardEvent } from "react";
import { SelectInstance } from "react-select";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Badge, DataRow, LoadingGroup, Spinner, DataId, Button, Buttons } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { debounce, EmptyArray } from "../../utilities";
import { createComponentStyleHook } from "../../tokens";
import { MultiSelectDataDropdownProps } from "./MultiSelectDataDropdown";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useId } from "../../hooks/useId";
import { SharedDropdown } from "../Dropdown/SharedDropdown";
import { InputContainer } from "../shared";
import { Dropdown } from "../Dropdown";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import tokenStyles from "../MultiSelectDropdown/styles.module.css";
import legacyStyles from "../MultiSelectDropdown/styles.module.scss";
import { Placeholder } from "../MultiSelectDropdown/Placeholder";
import { useSubmitting } from "../SubmitGroup";
import { useConsolidatedRows } from "../DataDropdown/useConsolidatedRows";
import { OptionItem } from "../Dropdown/OptionItem";

const useResolvedMultiSelectDataDropdownStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends MultiSelectDataDropdownProps<object> {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

export const Input = forwardRef(function Input(props: InputProps, ref: ForwardedRef<SelectInstance<OptionItem | null, boolean>>) {
  const {
    dataSource,
    searchField,
    optionLabelProvider,
    suffix,
    label,
    tooltip,
    instructions,
    defaultValue = null,
    hideInstructions,
    hideLabel,
    placeholder = "Select",
    required = false,
    onChange,
  } = props;

  const { mode, styles, StyleModeBoundary } = useResolvedMultiSelectDataDropdownStyles(props);

  const { value = defaultValue } = props;

  const id = useId();
  const selectedValues = useMemo(() => new Set(value ?? EmptyArray), [value]);

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const submitting = useSubmitting();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.MultiSelectDropdown, "multi-select-data-dropdown"],
    testIdPrefix: "multi-select-data-dropdown",
  });

  const [selectedRows, setSelectedRows] = useState<ReadonlyArray<DataRow<object>>>(EmptyArray);

  // We mandate that the data source must at minimum have 10 items per page to ensure scrolling works, etc.
  useEffect(() => {
    if (dataSource.pageSize < 10)
      dataSource.setPageSize(10);
  }, [dataSource.pageSize]);

  const rowState = useConsolidatedRows(dataSource);

  useEffect(() => {
    const doRowValueLoad = async () => {
      if (value !== null && value.length !== 0) {
        const rowLookup = new Map(selectedRows.map((row) => [row.id, row]));
        const availableRowsLookup = new Map(rowState.rows.map((row) => [row.id, row]));

        const neededRowKeys: Array<DataId> = [];
        let newSelectedRows: Array<DataRow<object>> = [];

        for (const key of value) {
          let row = rowLookup.get(key);
          if (!row) row = availableRowsLookup.get(key);

          if (row)
            newSelectedRows.push(row);
          else
            neededRowKeys.push(key);
        }

        if (neededRowKeys.length > 0) {
          const incomingRows = await dataSource.get(neededRowKeys);
          newSelectedRows = [
            ...newSelectedRows,
            ...incomingRows,
          ];
        }

        const newSelectedRowsLookup = new Map(newSelectedRows.map((row) => [row.id, row]));

        const removeSet = new Set<DataId>();
        for (const id of value) {
          if (!newSelectedRowsLookup.has(id)) {
            removeSet.add(id);
          }
        }

        if (removeSet.size > 0) {
          const newValue = value.filter((id) => !removeSet.has(id));
          onChange?.(newValue, value);
        } else setSelectedRows(newSelectedRows);
      } else
        setSelectedRows(EmptyArray);
    };

    if (dataSource.busy)
      return;

    doRowValueLoad();

  }, [dataSource.get, dataSource.busy, value]);

  const changeHandler = useCallback(
    (selectedValue: DataId | null) => {
      if (selectedValue === null) return;
      if (value?.includes(selectedValue)) return;

      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.([...(value ?? EmptyArray), selectedValue], value);
    },
    [value, disabled, submitting, readOnly, onChange],
  );

  const clearKeyBoardHandler = useCallback(
    (ev?: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      if (ev?.key === "Enter" || ev?.key === " ") {
        ev.preventDefault();
        onChange?.(EmptyArray, value);
      }
    },
    [value, disabled, submitting, readOnly, onChange],
  );

  const clearClickHandler = useCallback(
    (ev?: MouseEvent<HTMLButtonElement>) => {
      ev?.preventDefault();

      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(EmptyArray, value);
    },
    [value, disabled, submitting, readOnly, onChange],
  );

  const filteredOptions = useMemo(() => {
    return rowState.rows.map((row) => {
      const { id, data } = row;

      if (id === null || selectedValues.has(id)) return null;

      return (
        <Dropdown.Option key={id} value={id}>{optionLabelProvider(data)}</Dropdown.Option>
      );
    }).filter((row) => !!row);
  }, [rowState.rows, optionLabelProvider, value]);

  // force a retrieval of the next page, if we (a) have a next page and (b) we now have less than 10 filtered options.
  useEffect(() => {
    if (filteredOptions.length < 10 && dataSource.hasNextPage)
      dataSource.next();
  }, [filteredOptions.length, dataSource.status]);

  const removeClickHandler = useCallback(
    (removeValue: DataId) => {
      if (!value?.includes(removeValue)) return;

      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(
        (value ?? EmptyArray).filter((item) => item !== removeValue),
        value,
      );
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const searchChangeHandler = useCallback(debounce((value: string | null) => {
    if (searchField && !value)
      dataSource.filterBy(searchField, null);
    else if (searchField)
      dataSource.filterBy(searchField, value);
  }, 500), [dataSource, searchField]);

  const scrollHandler = useCallback(() => {
    if (dataSource.hasNextPage)
      dataSource.next();
  }, [dataSource]);

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder />}>
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          label={label}
          instructions={instructions}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          required={required}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <SharedDropdown
            ref={ref}
            id={id}
            value={null}
            label={label}
            onChange={changeHandler}
            readOnly={readOnly}
            placeholder={placeholder}
            searchable={!!searchField}
            manualSearchFilter
            disabled={(dataSource.status === "error") ? true : props.disabled}
            suffix={
              (dataSource.busy) ? (
                <div role="status" aria-live="polite" aria-busy={dataSource.busy}>
                  <Shelf>
                    {suffix}
                    <Spinner size="regular" aria-label="Loading options" />
                  </Shelf>
                </div>) : suffix
            }
            onSearchChange={searchChangeHandler}
            onMenuScrollToBottom={scrollHandler}
            aria-label={props["aria-label"]}
          >
            {filteredOptions}
          </SharedDropdown>
          {(selectedRows.length > 0) && (
            <>
              <div
                className="badge-container"
                role="list"
                aria-label="Selected Values"
                aria-live="polite"
                aria-relevant="additions removals"
              >
                {selectedRows.map((row) => {
                  const { id, data } = row;

                  return (
                    <div key={id} role="listitem">
                      <Badge
                        key={id}
                        disabled={readOnly || submitting || disabled}
                        buttonTooltip="Remove"
                        type="info"
                        onClick={() => removeClickHandler(id)}
                      >
                        {optionLabelProvider(data)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              {!readOnly && !disabled && (
                <Body className="clear-container" size="mini">
                  <Button
                    appearance={Buttons.Functional.Tertiary.Compact}
                    className="clear-button"
                    aria-label="Deselects all items"
                    onClick={clearClickHandler}
                    onKeyPress={clearKeyBoardHandler}
                  >
                    Clear all
                  </Button>
                </Body>
              )}
            </>
          )}
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
